import { useTranslation } from "react-i18next";
import {
  Box,
  Divider,
  Flex,
  useDisclosure,
  useToast,
  useBreakpointValue,
  Button,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverArrow,
  PopoverCloseButton,
  PopoverBody,
  Link,
  Text
} from "@chakra-ui/react";
import { DecarbonizationChart, ExportModal, Pathways } from "../../Decarbonization/components";
import React, { useEffect, useState } from "react";
import { CHART_COLORS, getRandomColor, VALIDATION_PATTERNS } from "../../Decarbonization/helpers";
import {
  useGetSupplierDecarbonizationChartData,
  useGetSupplierDecarbonization,
  useInitialiseSupplierDecarbonization,
  useUpdateSupplierEstimatedGrowth,
  useExportSupplierDecarbonizationData,
  useGetUserSpecificSupplier
} from "../../../hooks/query";
import {
  showErrorToast,
  useAsyncDebounce
} from "../../../utils";
import { hideAppSpinner, showAppSpinner } from "../../../context/Store/slices/appSpinnerSlice";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { NumericFormInputGroup } from "../../../components/Forms";
import { Card } from "../../../components/Card";
import { defaultDecarbonizationLevers, defaultDecarbonizationPathways } from "../helpers";

function SupplierDecarbonization() {
  const { t } = useTranslation("translation", {
    keyPrefix: "supplierNetZeroStrategy.decarbonization"
  });
  const contentDisplay = useBreakpointValue({
    base: "block",
    xl: "flex"
  });
  const toast = useToast();
  const dispatch = useDispatch();

  const {
    isOpen: isExportModalOpen,
    onOpen: onExportModalOpen,
    onClose: onExportModalClose
  } = useDisclosure();

  const [chartData, setChartData] = useState();
  const [decarbonizationData, setDecarbonizationData] = useState(null);
  const [isInitialised, setIsInitialised] = useState(false);

  // isDemo const should be exchanged with logic for supplier subscription when available
  const isDemo = true;

  const onGetDecarbSuccess = (data) => {
    if (!data.pathways || data.pathways.length === 0) {
      data.pathways = defaultDecarbonizationPathways;
    }
    if (!data.levers || data.levers.length === 0) {
      data.levers = defaultDecarbonizationLevers;
    }
    setDecarbonizationData(data);
    refetchChartData();
  };

  const {
    data: supplierData
  } = useGetUserSpecificSupplier({
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message)
  });

  useGetSupplierDecarbonization(supplierData?.id, {
    onSuccess: (data) => onGetDecarbSuccess(data),
    onError: err => handleDecarbError(err),
    cacheTime: 0,
    enabled: supplierData !== null && typeof supplierData !== "undefined"
  });

  const { mutate: initialise } = useInitialiseSupplierDecarbonization(supplierData?.id, {
    onMutate: () => dispatch(showAppSpinner()),
    onSuccess: (data) => onGetDecarbSuccess(data),
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner()),
    enabled: supplierData !== null && typeof supplierData !== "undefined"
  });

  useEffect(() => {
    if (chartData && chartData.baselineEmissionsTotal === 0 && !isInitialised) {
      setIsInitialised(true);
      initialise();
    }
  }, [chartData, initialise, isInitialised]);

  const handleDecarbError = (err) => {
    if (err.response?.status === 404) {
      setIsInitialised(true);
      return initialise();
    }
    showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message);
  };

  const {
    refetch: refetchChartData
  } = useGetSupplierDecarbonizationChartData(supplierData?.id, {
    enabled: decarbonizationData !== null && supplierData !== null && typeof supplierData !== "undefined",
    onSuccess: (data) => {
      let dataElements = [];
      data.data.forEach(el => {
        dataElements = dataElements.concat(el.data);
      });
      const maxValue = Math.max(...dataElements.map(el => el.y));
      const maxYear = Math.max(...dataElements.map(el => Number(el.x)));

      const coloredChartData = data.data.map(el => {
        return {
          ...el,
          color: CHART_COLORS[el.id] ?? getRandomColor()
        };
      });

      const newData = {
        baseYear: data.organization?.baseYear,
        companyName: data.organization?.name,
        baselineEmissionsTotal: data.baselineEmissionsTotal,
        chartData: coloredChartData,
        maxValue: maxValue + 0.05 * maxValue,
        maxYear: maxYear
      };
      setChartData(newData);
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    cacheTime: 0
  });

  const {
    register,
    formState: { errors: estimatedGrowthErrors },
    reset
  } = useForm({
    mode: "onChange",
    defaultValues: { estimatedYearlyEmissionsGrowth: decarbonizationData?.estimatedYearlyEmissionsGrowth ?? "" }
  });

  useEffect(() => {
    if (decarbonizationData) {
      reset({ estimatedYearlyEmissionsGrowth: decarbonizationData.estimatedYearlyEmissionsGrowth });
    }
  }, [reset, decarbonizationData]);

  const handleExportClick = () => {
    onExportModalOpen();
  };

  const handleExportModalClose = () => {
    onExportModalClose();
  };

  const handleExport = (exportYear) => {
    dispatch(showAppSpinner());
    exportDecarbonizationData(exportYear);
    onExportModalClose();
  };

  const handleExportErrors = (err) => {
    const parsedErrorObj = JSON.parse(String.fromCharCode.apply(null, new Int8Array(err.response.data)));
    showErrorToast(toast, parsedErrorObj?.message ?? err.message);
    dispatch(hideAppSpinner());
  };

  const {
    mutate: exportDecarbonizationData
  } = useExportSupplierDecarbonizationData(supplierData?.id, {
    onError: err => handleExportErrors(err),
    onSettled: () => dispatch(hideAppSpinner()),
    cacheTime: 0,
    enabled: supplierData !== null && typeof supplierData !== "undefined"
  });

  const estimatedGrowthRegister = {
    ...register("estimatedYearlyEmissionsGrowth", {
      required: VALIDATION_PATTERNS.DECIMAL_PERCENTAGE.message,
      pattern: VALIDATION_PATTERNS.DECIMAL_PERCENTAGE
    })
  };

  const delayUpdate = useAsyncDebounce((value) => {
    if (Object.keys(estimatedGrowthErrors).length === 0) {
      updateEstimatedGrowth(value);
    }
  }, 500);

  const { mutate: updateEstimatedGrowth } = useUpdateSupplierEstimatedGrowth(supplierData?.id, {
    onSuccess: (data) => onGetDecarbSuccess(data),
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    enabled: supplierData !== null && typeof supplierData !== "undefined"
  });

  return <>
    <Box marginBlockStart={5}>
      <Flex alignItems="end" justifyContent="space-between">
        <NumericFormInputGroup
          {...estimatedGrowthRegister}
          label={t("estimatedYearlyEmissionsGrowth")}
          labelFontSize="md"
          errors={estimatedGrowthErrors}
          type="number"
          addonText="%"
          width="100px"
          backgroundColor="white"
          onChange={(e) => {
            estimatedGrowthRegister.onChange(e);
            // Sending the update request to the server, but delayed with 500 ms (after the last keystroke)
            delayUpdate(e.target.value);
          }}
        />
        {!isDemo && <Button
          colorScheme="customScheme"
          type="submit"
          width="32"
          onClick={handleExportClick}
        >
          {t("export")}
        </Button>}
      </Flex>
      <Divider marginBlock={5} />
      <Flex display={contentDisplay} gap={5}>
        {decarbonizationData &&
          <Popover
            defaultIsOpen={true}
            placement={contentDisplay === "flex" ? "top-end" : "top"}
            closeOnBlur={false}
            trigger="hover"
            gutter={0}
          >
            <PopoverTrigger>
              <Box width={contentDisplay === "block" ? "100%" : "45%"}>
                <Card backgroundColor={"#d8d8d8"} opacity={0.4} pointerEvents="none">
                  <Pathways
                    data={decarbonizationData}
                    handleSwitchChange={() => {}}
                    handleModalOpen={() => {}}
                    isDemo={isDemo}
                  />
                </Card>
              </Box>
            </PopoverTrigger>

            <PopoverContent
              color="white" bg="customScheme.500" borderColor="customScheme.500"
              minWidth={"450px"} marginLeft={contentDisplay === "flex" ? "250px" : null}
            >
              <PopoverHeader fontWeight="bold" border="0">
                {t("fullAccessHeader")}
              </PopoverHeader>

              <PopoverArrow bg="blue.800" />

              <PopoverCloseButton />

              <PopoverBody>
                <Text size="md">
                  {t("fullAccessMessage") + " "}
                  <Link href="mailto: info@esg360.io" isExternal color={"esg_link"} fontWeight="semibold">
                    info@esg360.io
                  </Link>
                </Text>
              </PopoverBody>
            </PopoverContent>
          </Popover>
        }
        {chartData && <DecarbonizationChart
          width={contentDisplay === "block" ? "100%" : "55%"}
          blockSize={96}
          marginBlockStart={5}
          data={chartData}
          isDataAssociated={true}
          legends={[
            {
              anchor: "top-right",
              direction: "column",
              justify: false,
              translateX: 120,
              translateY: 0,
              itemsSpacing: 0,
              itemDirection: "left-to-right",
              itemWidth: 100,
              itemHeight: 34,
              itemOpacity: 1,
              symbolSize: 12,
              symbolShape: "circle",
              symbolBorderColor: "rgba(9, 9, 9, .5)",
              effects: [
                {
                  on: "hover",
                  style: {
                    itemBackground: "rgba(0, 0, 0, .03)",
                    itemOpacity: 1
                  }
                }
              ]
            }
          ]}
        />}
      </Flex>
    </Box>
    {decarbonizationData && <ExportModal
      baseYear={decarbonizationData.baseYear}
      isOpen={isExportModalOpen}
      onClose={handleExportModalClose}
      onSubmit={handleExport}
    />}
  </>;
}

export default SupplierDecarbonization;